import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, FileText, CheckCircle2, Lock } from 'lucide-react';
import { notifyFormSubmission } from '@/lib/notifyForm';
import { supabase } from '@/integrations/supabase/client';

type Lang = 'en' | 'fr' | 'es';

const T = {
  en: {
    badge: 'CONFIDENTIAL · NDA SAMPLE',
    title: 'Get the SOCRAT Sample Intelligence Briefing',
    sub: 'A redacted, anonymised version of a real Multi-Layer Resource Scan delivered to a junior lithium explorer in West Africa. 4 pages · top 12 drill targets · ROI math.',
    name: 'Full name',
    email: 'Work email',
    company: 'Company',
    role: 'Role (optional)',
    cta: 'Send me the report',
    sending: 'Preparing your report…',
    okTitle: 'Your report is ready',
    okSub: 'Click below to download. We also emailed you a copy and our team will reach out within 24h to answer questions.',
    download: 'Download Sample Report (PDF)',
    privacy: 'No spam. Confidential. Used only to send you the report and a single follow-up.',
  },
  fr: {
    badge: 'CONFIDENTIEL · ÉCHANTILLON NDA',
    title: 'Recevez le briefing d\'exemple SOCRAT',
    sub: 'Version anonymisée d\'un vrai Multi-Layer Resource Scan livré à un explorateur lithium junior en Afrique de l\'Ouest. 4 pages · top 12 cibles de forage · calcul ROI.',
    name: 'Nom complet',
    email: 'Email professionnel',
    company: 'Société',
    role: 'Fonction (optionnel)',
    cta: 'Envoyez-moi le rapport',
    sending: 'Préparation du rapport…',
    okTitle: 'Votre rapport est prêt',
    okSub: 'Téléchargez ci-dessous. Nous vous l\'avons aussi envoyé par email et notre équipe vous contacte sous 24 h.',
    download: 'Télécharger le rapport (PDF)',
    privacy: 'Pas de spam. Confidentiel. Utilisé uniquement pour vous envoyer le rapport et un seul suivi.',
  },
  es: {
    badge: 'CONFIDENCIAL · MUESTRA NDA',
    title: 'Recibe el informe de muestra SOCRAT',
    sub: 'Versión anonimizada de un Multi-Layer Resource Scan real entregado a un explorador junior de litio en África Occidental. 4 páginas · top 12 objetivos · análisis ROI.',
    name: 'Nombre completo',
    email: 'Email profesional',
    company: 'Empresa',
    role: 'Cargo (opcional)',
    cta: 'Enviarme el informe',
    sending: 'Preparando informe…',
    okTitle: 'Tu informe está listo',
    okSub: 'Descárgalo abajo. También te lo enviamos por email y nuestro equipo te contactará en 24h.',
    download: 'Descargar informe (PDF)',
    privacy: 'Sin spam. Confidencial. Solo para enviarte el informe y un seguimiento.',
  },
};

interface Props {
  open: boolean;
  onClose: () => void;
  lang?: Lang;
}

export default function SampleReportModal({ open, onClose, lang = 'en' }: Props) {
  const t = T[lang];
  const [form, setForm] = useState({ name: '', email: '', company: '', role: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      // Save lead
      await supabase.from('zone_analysis_requests').insert({
        full_name: form.name,
        email: form.email,
        company: form.company || null,
        country: 'Sample Report Request',
        zone_location: `Sample report download · role: ${form.role || 'n/a'}`,
        zone_size: 'sample-report',
        analysis_types: ['Sample Report Download'],
        report_type: 'Sample PDF Report',
        project_urgency: 'standard',
        how_found_us: 'sovereign-scout sample report gate',
      });
      notifyFormSubmission('Sovereign Scout — Sample Report Download', form);
    } catch (err) {
      console.error('Sample report capture error:', err);
    } finally {
      setDone(true);
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-lg w-full p-7 relative shadow-2xl"
          >
            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X className="h-5 w-5" />
            </button>

            {!done ? (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-5 w-5 text-sovereign-navy" />
                  <span className="text-[10px] font-bold tracking-widest text-sovereign-gold">{t.badge}</span>
                </div>
                <h2 className="text-2xl font-extrabold text-sovereign-navy mb-2">{t.title}</h2>
                <p className="text-sm text-gray-600 mb-5 leading-relaxed">{t.sub}</p>

                <form onSubmit={submit} className="space-y-3">
                  <input
                    required
                    type="text"
                    placeholder={t.name}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-sovereign-navy outline-none text-sm min-h-[44px]"
                  />
                  <input
                    required
                    type="email"
                    placeholder={t.email}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-sovereign-navy outline-none text-sm min-h-[44px]"
                  />
                  <input
                    required
                    type="text"
                    placeholder={t.company}
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-sovereign-navy outline-none text-sm min-h-[44px]"
                  />
                  <input
                    type="text"
                    placeholder={t.role}
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-sovereign-navy outline-none text-sm min-h-[44px]"
                  />

                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-sovereign-navy text-white py-3.5 rounded-lg font-bold border-2 border-sovereign-gold hover:bg-sovereign-gold hover:text-sovereign-navy transition-colors disabled:opacity-60 min-h-[48px] flex items-center justify-center gap-2"
                  >
                    {sending ? t.sending : (<><Lock className="h-4 w-4" /> {t.cta}</>)}
                  </button>
                  <p className="text-[11px] text-gray-500 text-center leading-relaxed pt-1">{t.privacy}</p>
                </form>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-extrabold text-sovereign-navy mb-2">{t.okTitle}</h2>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">{t.okSub}</p>
                <a
                  href="/socrat-sample-report.pdf"
                  download="SOCRAT-Sample-Intelligence-Briefing.pdf"
                  className="inline-flex items-center gap-2 bg-sovereign-gold text-sovereign-navy px-6 py-3.5 rounded-lg font-bold hover:bg-yellow-400 transition-colors min-h-[48px]"
                >
                  <Download className="h-5 w-5" />
                  {t.download}
                </a>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
